import React, { useEffect, useState } from "react";
import { Input, Modal, Spin } from "antd";
import { useSelector } from "react-redux";

import * as issuesApi from "../../../../core/data/redux/services/issuesApi";
import { selectActiveCompany } from "../../../../core/data/redux/projects/selectors";
import UserAvatar from "../../components/UserAvatar";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";
import { errMsg } from "../../helpers/errMsg.helper";
import { CONFIRM_MODAL_PROPS } from "../../helpers/modal.style";
import { notify } from "../../../../services/toast.config";

import type { Comment } from "../../../../core/data/redux/projects/types";

interface CommentThreadProps {
  issueId: number;
  currentUserId: number | null;
}

const CommentThread: React.FC<CommentThreadProps> = ({
  issueId,
  currentUserId,
}) => {
  const activeCompany = useSelector(selectActiveCompany);

  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingBody, setEditingBody] = useState("");

  const reload = async () => {
    if (!activeCompany.company_id) return;
    setLoading(true);
    try {
      const data = await issuesApi.listComments(
        activeCompany.company_id,
        activeCompany.company_type,
        issueId
      );
      setComments(Array.isArray(data?.comments) ? data.comments : []);
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to load comments"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueId, activeCompany.company_id]);

  const handlePost = async () => {
    if (!body.trim()) return;
    try {
      setSubmitting(true);
      await issuesApi.createComment(
        activeCompany.company_id,
        activeCompany.company_type,
        { issue_id: issueId, body: body.trim() }
      );
      setBody("");
      await reload();
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to post comment"));
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (c: Comment) => {
    setEditingId(c.id);
    setEditingBody(c.body);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingBody("");
  };

  const saveEdit = async () => {
    if (editingId == null) return;
    try {
      await issuesApi.updateComment(
        activeCompany.company_id,
        activeCompany.company_type,
        editingId,
        editingBody.trim()
      );
      cancelEdit();
      await reload();
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to update comment"));
    }
  };

  const handleDelete = (c: Comment) => {
    Modal.confirm({
      title: "Delete this comment?",
      okText: "Delete",
      okButtonProps: { danger: true },
      ...CONFIRM_MODAL_PROPS,
      onOk: async () => {
        try {
          await issuesApi.deleteComment(
            activeCompany.company_id,
            activeCompany.company_type,
            c.id
          );
          await reload();
        } catch (e: any) {
          notify.error(errMsg(e, "Failed to delete comment"));
        }
      },
    });
  };

  return (
    <div>
      <div className="mb-3">
        <Input.TextArea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add a comment…"
          rows={3}
          disabled={submitting}
        />
        <div className="mt-2 d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={handlePost}
            disabled={submitting || !body.trim()}
          >
            {submitting ? "Posting…" : "Post Comment"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-3">
          <Spin />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-muted mb-0">No comments yet.</p>
      ) : (
        <ul className="list-unstyled mb-0 d-flex flex-column gap-3">
          {comments.map((c) => {
            const isOwn = currentUserId != null && c.author_id === currentUserId;
            return (
              <li key={c.id} className="d-flex gap-2">
                <UserAvatar
                  name={c.author_snapshot?.name}
                  img={c.author_snapshot?.img}
                  size={32}
                  fontSize={13}
                />
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2">
                    <strong>
                      {c.author_snapshot?.name || `User #${c.author_id}`}
                    </strong>
                    <span className="text-muted small">
                      {humanReadableDate(c.created_at)}
                    </span>
                    {isOwn && editingId !== c.id && (
                      <span className="ms-auto d-flex gap-1">
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0"
                          onClick={() => startEdit(c)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0 text-danger"
                          onClick={() => handleDelete(c)}
                        >
                          Delete
                        </button>
                      </span>
                    )}
                  </div>
                  {editingId === c.id ? (
                    <div>
                      <Input.TextArea
                        value={editingBody}
                        onChange={(e) => setEditingBody(e.target.value)}
                        rows={3}
                      />
                      <div className="mt-2 d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={saveEdit}
                          disabled={!editingBody.trim()}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-light"
                          onClick={cancelEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="text-body"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {c.body}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CommentThread;
